/**
 * Menu — the dismiss-on-outside-click popover, and the rows that go in it.
 *
 * Lifted out of `GroupBar`, which is where every line of it was learned, when
 * the chart PIP's view strip needed the same overflow dropdown. A second copy
 * was the alternative and a bad one: the three things this gets right are all
 * things that fail *silently* somewhere else, and a copy only inherits them
 * until someone fixes one of them in one place.
 *
 * The PIP has the same shape of problem for the same reasons — it lives inside
 * the chart's clipping overlay, and its trigger sits on a canvas that eats
 * pointer events.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";

export function CloseIcon({ size = 11 }: { size?: number }) {
  return (
    // Drawn rather than the `×` character: the glyph's size and vertical
    // centring are the font's to decide, and it sits high in most of them.
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="2.5" y1="2.5" x2="9.5" y2="9.5" />
      <line x1="9.5" y1="2.5" x2="2.5" y2="9.5" />
    </svg>
  );
}

/**
 * A dismiss-on-outside-click popover, positioned `fixed` off its trigger.
 *
 * **Not `absolute`.** The bar sets `overflow: hidden` — it has to, or the chips
 * it is measuring would spill out of it — and an absolutely-positioned child is
 * clipped by exactly that. `fixed` takes the menu out of the bar's clipping
 * context entirely, at the cost of having to measure the trigger and follow it.
 * `FieldRef` in screener-grid solves the same problem the same way, for the
 * same reason.
 *
 * Flips above when there is not room below, so a bar near the bottom of the
 * window still shows its menu.
 *
 * `anchorSelector` exists for the type tick-list, whose trigger is one of
 * several buttons that are re-rendered as the list is ticked: a ref would point
 * at a detached node the moment a tick changed the button's label, so the
 * anchor is re-found by selector inside `root` each time it is measured.
 */
export function Menu({
  anchorRef,
  anchorSelector,
  root,
  children,
  onClose,
  align = "left",
  title,
  tourId,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  anchorSelector?: string;
  root?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  onClose: () => void;
  align?: "left" | "right";
  title?: string;
  /**
   * Marks this menu with `data-tour`, so the product tour can point at it.
   *
   * Opt-in per call site rather than a blanket attribute on every menu: the
   * tour finds a surface by selector, and `[data-tour="menu"]` on all of them
   * would match whichever happened to be open. Only the menus a step names
   * carry one. See lib/tour.ts.
   */
  tourId?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor =
      (anchorSelector && root?.current?.querySelector<HTMLElement>(anchorSelector)) ||
      anchorRef.current;
    const menu = ref.current;
    if (!anchor || !menu) return;

    const rect = anchor.getBoundingClientRect();
    const height = menu.offsetHeight;
    const width = menu.offsetWidth;

    const below = rect.bottom + 4;
    const flip = below + height > window.innerHeight && rect.top - height - 4 > 0;
    const top = flip ? rect.top - height - 4 : below;

    let left = align === "right" ? rect.right - width : rect.left;
    // Keep it on screen horizontally too — the overflow menu is wide and the
    // bar can sit hard against the right edge of a narrow screener column.
    left = Math.max(4, Math.min(left, window.innerWidth - width - 4));

    setPos({ top, left });
  }, [anchorRef, anchorSelector, root, align, children]);

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    // `pointerdown`, not `mousedown`. SciChart calls `preventDefault()` on its
    // own pointer events, which suppresses the compatibility mouse events
    // entirely — so a `mousedown` listener never fires for a click on a chart,
    // and every popover in the app stayed open when you clicked the candles to
    // dismiss it. Capture phase as well, so a handler inside the popover that
    // stops propagation cannot hide the outside click that follows.
    document.addEventListener("pointerdown", onDown, true);
    // A fixed element does not travel with a scrolling ancestor, so it would
    // detach from its trigger. Closing is honest and cheaper than tracking.
    //
    // **But not when the popover scrolls itself.** This listens in the capture
    // phase on `window`, which sees scroll from *every* element — including the
    // popover's own list. So scrolling the Themes menu, which is long enough to
    // need it, dismissed the menu: the scrollbar was unusable and a drag on it
    // closed the thing being dragged.
    //
    // The intent was always "an ancestor moved under us". A scroll that starts
    // inside the popover has not moved anything the popover is positioned
    // against, so it is ignored.
    const onScroll = (event: Event) => {
      if (ref.current?.contains(event.target as Node)) return;
      onClose();
    };
    const onResize = () => onClose();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-tour={tourId}
      style={{
        position: "fixed",
        // Rendered off-screen for the first frame so it can be measured before
        // it is placed; `visibility` rather than `display`, which has no size.
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
        zIndex: 1000,
        background: "var(--qf-surface)",
        border: "1px solid var(--qf-border)",
        borderRadius: 6,
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        minWidth: 186,
        maxHeight: "60vh",
        overflowY: "auto",
      }}
    >
      {title !== undefined && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "6px 6px 6px 10px",
            background: "var(--qf-surface-alt)",
            borderBottom: "1px solid var(--qf-border)",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--qf-text-secondary)",
            fontWeight: 600,
          }}
        >
          <span>{title}</span>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            style={{
              width: 20,
              height: 20,
              flex: "0 0 auto",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              border: 0,
              borderRadius: 4,
              background: "transparent",
              color: "var(--qf-text-secondary)",
              cursor: "pointer",
            }}
          >
            <CloseIcon />
          </button>
        </div>
      )}
      {children}
    </div>
  );
}

export function MenuRow({
  children,
  active,
  onSelect,
  indent,
}: {
  children: React.ReactNode;
  active: boolean;
  onSelect: () => void;
  /** An option under a section heading: inset so the heading reads as its title. */
  indent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
        font: "inherit",
        fontSize: 12,
        padding: indent ? "6px 10px 6px 22px" : "6px 10px",
        border: 0,
        background: active ? "var(--qf-selection)" : "transparent",
        color: "var(--qf-text)",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      {children}
    </button>
  );
}
